import React, { useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';
import { RadioGroupField } from 'components/form';
import { FormSectionHeader } from 'components/shared/FormSectionHeader';
import {
  deskingConfigByAccId,
  DeskingPanel,
  FinanceProgramFormValues,
  FormSections,
  deskingSelectors,
} from 'modules/desking';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { useDebounce } from 'hooks/useDebounce';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { DealBuilderFormValues } from '../../../types';
import { DESKING_SYNC_DEBOUNCE_MS, SALE_TYPE_OPTIONS } from '../../../constants';
import { useDeskingFormSync } from '../../../hooks/useDeskingFormSync';

const saleTypeRadioOptions = SALE_TYPE_OPTIONS.map(({ value, label, description }) => ({
  value,
  label: (
    <Stack direction="row" gap={0.5} alignItems="center">
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  ),
}));

export const DealBuilderDesking = () => {
  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const aftermarketPortalConfigurations = useSelector(portalConfigurationsSelectors.getAftermarket);
  const dealBuilderDeskingData = useSelector(deskingSelectors.dealBuilderDeskingData, shallowEqual);

  const {
    sourceData: { vo, creditApplication },
    activeEntity,
    equipment: { selectedEquipmentItem },
  } = useDealBuilderContext();
  const { control } = useFormContext<DealBuilderFormValues>();

  const saleType = useWatch({ control, name: 'financeMenu.saleType' });

  const [deskingChange, setDeskingChange] = useState<{ formValues: FinanceProgramFormValues } | null>(null);

  const debouncedDeskingChange = useDebounce(deskingChange, DESKING_SYNC_DEBOUNCE_MS);
  const debouncedDealBuilderDeskingData = useDebounce(dealBuilderDeskingData, DESKING_SYNC_DEBOUNCE_MS);

  const [hasSavedDeskingData] = useState(() => !!activeEntity?.proposal?.settings?.configuration?.pricing);

  useDeskingFormSync(debouncedDeskingChange?.formValues ?? null, debouncedDealBuilderDeskingData, hasSavedDeskingData);

  const handleDeskingFormValuesChanged = (formValues: FinanceProgramFormValues) => {
    setDeskingChange({ formValues });
  };

  return (
    <Stack gap={1}>
      <Stack gap={1}>
        <FormSectionHeader name="Sale Type" value="saleType" />
        <RadioGroupField row control={control} name="financeMenu.saleType" options={saleTypeRadioOptions} />
      </Stack>

      <DeskingPanel
        vo={vo}
        accountId={account.id}
        creditApplication={creditApplication}
        userEmail={userProfile.email}
        proposalId={hasSavedDeskingData ? (activeEntity?.proposal.proposalId ?? null) : null}
        isProposalLoading={false}
        externalSaleType={saleType}
        externalEquipmentId={selectedEquipmentItem?.equipment?.id ?? null}
        onCreateProposalProduct={async () => null}
        onSubmitDesking={async () => {}}
        configurationByAccount={deskingConfigByAccId[account.id]}
        isProductCreationAvailable={aftermarketPortalConfigurations?.allowCreateCustomProducts}
        customSections={{
          [FormSections.Contact]: { isHidden: true },
          [FormSections.SaleDetails]: { isHidden: true },
          [FormSections.Equipment]: { isHidden: true },
          [FormSections.SendVia]: { isHidden: true },
          [FormSections.NotesToBuyer]: { isHidden: true },
        }}
        panelFormCustomActions={{
          isHidden: true,
        }}
        onFormValuesChanged={handleDeskingFormValuesChanged}
      />
    </Stack>
  );
};
